import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {SizeSelection_Size} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {TrackingRecoElement} from '@aboutyou/legacy-tracking-sdk';
import {useHistory} from '@aboutyou/router';
import {getAdditionalBasketData} from '@aboutyou/tracking';
import {
  AddToBasketTrackingData,
  BasketMutationError,
  BasketMutationSuccess,
  useAddToBasketMutation,
  useIsSizeInBasket,
  useQuantityInBasketBySizeId,
} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {useCallback, useRef, useState} from 'react';
import {useA2bTest} from '../../../../client-services/InitializeAbTestsConditionally/aa_tests_for_testing_the_setup';
import {Captcha} from '../../../../features/Captcha/Captcha';
import {useActivatedCouponState} from '../../../../features/Coupons/useActivatedCouponState';
import {useTrackFitAnalytics} from '../../../../features/SizeAdvisor/useTrackFitAnalytics';
import {SizeSource} from '../../../../features/SizeSelection/hooks/useSelectedSizeState';
import {useAddToBasketErrorToast} from '../../../../features/SizeSelectionFlyout/hooks/useAddToBasketErrorToast';
import {
  SizeSelectionOpenedStateKey,
  useSizeSelectionOpenedState,
} from '../../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {useInlineSizeSelectionQuickViewAB} from '../../AddToBasketQuickView/hooks/useInlineSizeSelectionQuickViewAB';
import {isAddToBasketDisabledForSize} from '../isAddToBasketDisabledForSize';
import {AddToBasketAndWishlistButtons} from './AddToBasketAndWishlistButtons';
import {AddToBasketQuickViewButton} from './AddToBasketQuickViewButton';
import {ErrorNotification} from './ErrorNotification';
import {
  AUTO_ADD_TO_BASKET_LOCATION_ID,
  shouldAutoAddToBasket,
  useAutoAddToBasketEffect,
} from './hooks/useAutoAddToBasketEffect';
import {setFitAnalyticsUsedValue} from './utils/setFitAnalyticsUsedValue';

interface AddToBasketProps {
  size?: SizeSelection_Size & SizeSource;
  product: Product;
  brandName: string;
  onAddToBasketSuccess?: (props: Omit<BasketMutationSuccess, 'failed'>) => void;
  disableBasketRecommendations?: boolean;
  inlineSizeSelection: boolean;
  wishlistComponent?: React.ReactNode | null;
  sizeSelectionOpenedStateKey: SizeSelectionOpenedStateKey;
  recommendedSize?: SizeSelection_Size;
  isQuickView?: boolean;
  isOneSize?: boolean;
  isNewMobileQuickViewActive?: boolean;
  trackingData?: {
    sectionIndex?: number;
    sortingChannel?: string;
    pageType?: string;
    recoType?: string;
    elements?: TrackingRecoElement[];
    brandName?: string;
  };
  sizeFinderRecommendationsCallback?: (
    selectedSizeId: number,
  ) => string | undefined;
  overrideBETrackingData?: AddToBasketTrackingData;
  badgesList: string[];
}

export const AddToBasket: React.FC<AddToBasketProps> = ({
  size,
  product,
  brandName,
  onAddToBasketSuccess,
  inlineSizeSelection,
  wishlistComponent,
  sizeSelectionOpenedStateKey = SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION,
  recommendedSize,
  isQuickView,
  isOneSize,
  isNewMobileQuickViewActive,
  trackingData,
  sizeFinderRecommendationsCallback,
  overrideBETrackingData,
  badgesList,
}) => {
  const history = useHistory();

  const isAutoAdd = shouldAutoAddToBasket(history, size);

  const recoTypeValue = trackingData?.recoType;
  const defaultRecoType = recoTypeValue?.trim() || 'product';

  const addToBasket = useAddToBasketMutation({
    trackingDataGA: {
      ...trackingData,
      ...(isAutoAdd
        ? {
            recoType: AUTO_ADD_TO_BASKET_LOCATION_ID,
            elements: trackingData?.elements ?? [],
          }
        : {
            recoType: defaultRecoType,
            elements: [],
          }),
      brandName,
      badges: badgesList,
    },
  });
  const [isNoSizeSelectedErrorShown, setIsNoSizeSelectedErrorShown] =
    useState(false);
  const showAddToBasketError = useAddToBasketErrorToast();
  const {open: openSizeSelection} = useSizeSelectionOpenedState(
    sizeSelectionOpenedStateKey,
  );

  const loadingBasket = useRef(false);
  const inlineSizeSelectionQuickViewAB = useInlineSizeSelectionQuickViewAB();
  const {couponPromotionId} = useActivatedCouponState(product.id);

  // captcha required can also be set by API even if product data is not signaling it, so it must be a settable state
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(
    product.captchaRequired,
  );

  const [isAskingForCaptcha, setIsAskingForCaptcha] = useState(false);

  const isInBasket = useIsSizeInBasket();
  const quantityAddedInBasket = useQuantityInBasketBySizeId(size?.sizeId);
  const trackFitAnalytics = useTrackFitAnalytics(product);

  const isAddToBasketDisabled =
    loadingBasket.current ||
    isAddToBasketDisabledForSize(size, quantityAddedInBasket);

  const {activateAbTest} = useA2bTest();

  const handleAddToBasketSuccess = useCallback(
    ({
      freeDeliverySection,
      statusSuccessSections,
    }: {
      freeDeliverySection?: FreeDeliverySection;
      statusSuccessSections: AddItemResponseStatus_Success_Section[];
    }) => {
      setFitAnalyticsUsedValue(trackFitAnalytics, {
        selectedSize: size,
        recommendedSize,
      });

      activateAbTest();

      if (onAddToBasketSuccess) {
        onAddToBasketSuccess({freeDeliverySection, statusSuccessSections});
      }
    },
    [
      activateAbTest,
      onAddToBasketSuccess,
      recommendedSize,
      size,
      trackFitAnalytics,
    ],
  );

  const handleAddToBasketError = useCallback(
    (error: BasketMutationError) => {
      if (error.captchaFailed) {
        setIsCaptchaRequired(true);
        setIsAskingForCaptcha(true);
        if (isAskingForCaptcha) {
          showAddToBasketError(error);
        }
      } else {
        showAddToBasketError(error);
      }
    },
    [isAskingForCaptcha, showAddToBasketError],
  );

  const handleAddToBasket = useCallback(
    async (captchaCode?: string): Promise<boolean | void> => {
      if (!size) {
        if (!inlineSizeSelection) {
          openSizeSelection();
        } else {
          setIsNoSizeSelectedErrorShown(true);
        }
        return;
      }

      if (isAddToBasketDisabled) {
        return;
      }

      if (inlineSizeSelection) {
        setIsNoSizeSelectedErrorShown(false);
      }

      if (isCaptchaRequired && !captchaCode && !isInBasket(size.sizeId)) {
        setIsAskingForCaptcha(true);
        return;
      }

      setIsAskingForCaptcha(false);
      loadingBasket.current = true;

      const getCustomData = getAdditionalBasketData();

      const customData =
        getCustomData &&
        product &&
        getCustomData({
          productId: product.id,
          variantId: size.sizeId,
          productSponsoredType: product.sponsoredType,
        });

      const sizeFinderRecommendations = sizeFinderRecommendationsCallback?.(
        size.sizeId,
      );

      const trackingDataBEPayload = {
        ...customData,
        ...overrideBETrackingData,
      };

      return addToBasket({
        productId: product?.id,
        sizeId: size?.sizeId,
        captchaCode,
        trackingDataBE: trackingDataBEPayload,
        sizeSource: size.sizeSource,
        couponPromotionId,
        sizeFinderRecommendations,
      })
        .then(returnedValue => {
          if (returnedValue && returnedValue.failed) {
            handleAddToBasketError(returnedValue);
          } else {
            const {freeDeliverySection, statusSuccessSections} =
              returnedValue as BasketMutationSuccess;

            // When auto add to basket is enabled, we don't want to show the success flyout
            // since we navigate to the basket page in `useAutoAddToBasketEffect`
            if (!shouldAutoAddToBasket(history, size)) {
              handleAddToBasketSuccess({
                freeDeliverySection,
                statusSuccessSections,
              });
              setIsAskingForCaptcha(false);
            }

            return true;
          }
        })
        .catch(err => {
          handleAddToBasketError(err);
        })
        .finally(() => {
          loadingBasket.current = false;
        });
    },
    [
      size,
      isAddToBasketDisabled,
      inlineSizeSelection,
      isCaptchaRequired,
      isInBasket,
      product,
      addToBasket,
      couponPromotionId,
      openSizeSelection,
      handleAddToBasketError,
      handleAddToBasketSuccess,
      history,
    ],
  );

  useAutoAddToBasketEffect(handleAddToBasket, size);

  const handleCaptchaSuccess = useCallback(
    (captchaCode: string) => {
      handleAddToBasket(captchaCode);
    },
    [handleAddToBasket],
  );

  let AddToBasketViewComponent = (
    <AddToBasketAndWishlistButtons
      inlineSizeSelection={inlineSizeSelection}
      isProcessing={loadingBasket.current}
      isAddToBasketDisabled={isAddToBasketDisabled}
      handleAddToBasket={handleAddToBasket}
      wishlistComponent={wishlistComponent}
      isInBasket={isInBasket}
      size={size}
    />
  );

  const onAddToBasketClick = () => {
    if (inlineSizeSelectionQuickViewAB) {
      handleAddToBasket();
    } else {
      if (isOneSize) {
        handleAddToBasket();
      } else {
        openSizeSelection();
      }
    }
  };

  if (isQuickView && isNewMobileQuickViewActive) {
    AddToBasketViewComponent = (
      <AddToBasketQuickViewButton
        isProcessing={loadingBasket.current}
        isInBasket={isInBasket}
        size={size}
        onClick={onAddToBasketClick}
        isOneSize={isOneSize}
      />
    );
  }

  return (
    <ContentWrapper>
      {inlineSizeSelection && (
        <ErrorNotification isShown={isNoSizeSelectedErrorShown} />
      )}

      {isAskingForCaptcha ? (
        <StyledCaptcha
          onSuccess={handleCaptchaSuccess}
          uniqueKey="addToBasket"
        />
      ) : null}

      {AddToBasketViewComponent}
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex: 1 1 100%;
  flex-direction: column;
`;

const StyledCaptcha = styled(Captcha)`
  margin-top: 22px;
`;
